import { buildCsv, decodeCsvCell } from "./csv";
import { isUnsafeObjectKey } from "./text";
import type { LocalizedLabel } from "./types";

/**
 * The administrator-maintained translation dictionary. English is the master
 * language; other languages fall back to it when empty.
 */
export const DICTIONARY_SECTIONS = ["arenas", "ageGroups", "interface", "configuration"] as const;
export type DictionarySection = (typeof DICTIONARY_SECTIONS)[number];
export type Translations = Record<string, string>;
export type Dictionary = Partial<Record<DictionarySection, Record<string, Translations>>>;

export interface Language {
  code: string;
  name: string;
}

export const DEFAULT_LANGUAGES: Language[] = [
  { code: "no", name: "Norsk" },
  { code: "en", name: "English" },
];

const LANGUAGE_CODE = /^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/;

export type LanguageSettingsError = "languages" | "dictionary";

export function getLanguageSettingsValidationError(
  languages: unknown,
  dictionary: unknown,
): LanguageSettingsError | null {
  if (!Array.isArray(languages) || languages.length < 1) return "languages";
  const codes = new Set<string>();
  for (const language of languages as Language[]) {
    if (
      !language ||
      typeof language.code !== "string" ||
      !LANGUAGE_CODE.test(language.code) ||
      codes.has(language.code) ||
      typeof language.name !== "string" ||
      !language.name.trim()
    )
      return "languages";
    codes.add(language.code);
  }
  if (!codes.has("en")) return "languages";
  if (!dictionary || typeof dictionary !== "object" || Array.isArray(dictionary))
    return "dictionary";
  const dict = dictionary as Record<string, unknown>;
  if (
    Object.keys(dict).some(
      (section) => !(DICTIONARY_SECTIONS as readonly string[]).includes(section),
    )
  )
    return "dictionary";
  for (const section of DICTIONARY_SECTIONS) {
    const entries = dict[section];
    if (entries === undefined && (section === "interface" || section === "configuration")) continue;
    if (!entries || typeof entries !== "object" || Array.isArray(entries)) return "dictionary";
    for (const [key, translations] of Object.entries(entries as Record<string, unknown>)) {
      if (
        !key.trim() ||
        isUnsafeObjectKey(key) ||
        !translations ||
        typeof translations !== "object" ||
        Array.isArray(translations)
      )
        return "dictionary";
      for (const [code, text] of Object.entries(translations as Record<string, unknown>)) {
        if (!codes.has(code) || typeof text !== "string" || (code === "en" && !text.trim()))
          return "dictionary";
      }
    }
  }
  return null;
}

/** Drops one language's translations from every entry. */
export function removeDictionaryLanguage(dictionary: Dictionary, code: string): Dictionary {
  return Object.fromEntries(
    Object.entries(dictionary).map(([section, entries]) => [
      section,
      Object.fromEntries(
        Object.entries(entries ?? {}).map(([key, translations]) => [
          key,
          Object.fromEntries(
            Object.entries(translations).filter(([language]) => language !== code),
          ),
        ]),
      ),
    ]),
  );
}

/**
 * Resolves a label through the dictionary. An explicit (even empty)
 * translation wins over the built-in label; empty falls back to English.
 */
export function getDictionaryLabel(
  dictionary: Dictionary,
  section: DictionarySection,
  key: string,
  label: Partial<LocalizedLabel>,
  language: string,
): string {
  const translations = dictionary[section]?.[key];
  const master = translations?.en?.trim() || label.en || label.no || "";
  if (translations && Object.hasOwn(translations, language))
    return translations[language]!.trim() || master;
  return label[language] || master;
}

// ---------------------------------------------------------------------------
// CSV export/import (Excel-friendly, formula-injection safe)
// ---------------------------------------------------------------------------

export type LanguageCsvError =
  | "settings"
  | "syntax"
  | "header"
  | "names"
  | "columns"
  | "key"
  | "duplicate"
  | "unregistered"
  | "english";

class CsvError extends Error {
  constructor(readonly code: LanguageCsvError) {
    super(code);
  }
}

/** Marker for "no translation" (distinct from an empty, explicit fallback). */
const MISSING = "\\N";

export function exportLanguageCsv(languages: Language[], dictionary: Dictionary): string {
  if (getLanguageSettingsValidationError(languages, dictionary)) throw new CsvError("settings");
  const codes = [
    "en",
    ...languages.map((language) => language.code).filter((code) => code !== "en"),
  ];
  const rows: string[][] = [
    ["section", "key", ...codes],
    [
      "languages",
      "name",
      ...codes.map((code) => languages.find((language) => language.code === code)!.name),
    ],
  ];
  for (const section of DICTIONARY_SECTIONS) {
    for (const [key, translations] of Object.entries(dictionary[section] ?? {})) {
      rows.push([
        section,
        key,
        ...codes.map((code) =>
          Object.hasOwn(translations, code) ? translations[code]!.replace(/^\\/, "\\\\") : MISSING,
        ),
      ]);
    }
  }
  return buildCsv(rows);
}

/** RFC 4180-style parser accepting `;` (Excel, Nordic locale) or `,`. */
export function parseLanguageCsv(input: string): string[][] {
  const text = input.replace(/^\uFEFF/, "");
  const delimiter = text.startsWith('"section";') || text.startsWith("section;") ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let state: "start" | "plain" | "quoted" | "closed" = "start";
  const finishCell = () => {
    row.push(decodeCsvCell(cell));
    cell = "";
    state = "start";
  };
  const finishRow = () => {
    finishCell();
    if (row.some((value) => value !== "")) rows.push(row);
    row = [];
  };
  for (let index = 0; index < text.length; index++) {
    const char = text[index]!;
    if (state === "quoted") {
      if (char !== '"') cell += char;
      else if (text[index + 1] === '"') {
        cell += '"';
        index++;
      } else state = "closed";
    } else if (char === delimiter) finishCell();
    else if (char === "\r" || char === "\n") {
      finishRow();
      if (char === "\r" && text[index + 1] === "\n") index++;
    } else if (char === '"' && state === "start") state = "quoted";
    else {
      if (char === '"' || state === "closed") throw new CsvError("syntax");
      cell += char;
      state = "plain";
    }
  }
  if (state === "quoted") throw new CsvError("syntax");
  if (cell || row.length || state === "closed") finishRow();
  return rows;
}

export interface LanguageCsvImportResult {
  languages: Language[];
  dictionary: Dictionary;
  entryCount: number;
  newLanguageCount: number;
}

/**
 * Merges a CSV export back into the settings. Arena and age-group keys must
 * already be registered; English may not be emptied.
 */
export function importLanguageCsv(
  text: string,
  languages: Language[],
  dictionary: Dictionary,
  registeredEntries: { arenas: string[]; ageGroups: string[] },
): LanguageCsvImportResult {
  if (getLanguageSettingsValidationError(languages, dictionary)) throw new CsvError("settings");
  const [header, metadata, ...rows] = parseLanguageCsv(text);
  if (
    !header ||
    header[0] !== "section" ||
    header[1] !== "key" ||
    header.length < 3 ||
    new Set(header).size !== header.length
  )
    throw new CsvError("header");
  const codes = header.slice(2);
  if (!codes.includes("en") || codes.some((code) => !LANGUAGE_CODE.test(code)))
    throw new CsvError("header");
  if (
    !metadata ||
    metadata.length !== header.length ||
    metadata[0] !== "languages" ||
    metadata[1] !== "name" ||
    metadata.slice(2).some((name) => !name.trim())
  )
    throw new CsvError("names");

  const mergedLanguages = languages.map((language) => ({ ...language }));
  codes.forEach((code, index) => {
    if (!mergedLanguages.some((language) => language.code === code)) {
      mergedLanguages.push({ code, name: metadata[index + 2]!.trim() });
    }
  });
  const merged: Dictionary = structuredClone(dictionary);
  for (const section of DICTIONARY_SECTIONS) merged[section] ??= {};
  const seen = new Set<string>();
  for (const row of rows) {
    if (row.length !== header.length) throw new CsvError("columns");
    const [section = "", key = "", ...values] = row;
    if (
      !(DICTIONARY_SECTIONS as readonly string[]).includes(section) ||
      !key.trim() ||
      isUnsafeObjectKey(key)
    ) {
      throw new CsvError("key");
    }
    const identity = JSON.stringify([section, key]);
    if (seen.has(identity)) throw new CsvError("duplicate");
    seen.add(identity);
    const typedSection = section as DictionarySection;
    if (
      (typedSection === "arenas" || typedSection === "ageGroups") &&
      !registeredEntries[typedSection].includes(key)
    ) {
      throw new CsvError("unregistered");
    }
    const translations = { ...merged[typedSection]![key] };
    codes.forEach((code, index) => {
      const value = values[index] ?? "";
      if (value === MISSING) return;
      if (code === "en" && !value.trim()) throw new CsvError("english");
      translations[code] = value.startsWith("\\") ? value.replace(/^\\\\/, "\\") : value;
    });
    merged[typedSection]![key] = translations;
  }
  if (getLanguageSettingsValidationError(mergedLanguages, merged)) throw new CsvError("settings");
  return {
    languages: mergedLanguages,
    dictionary: merged,
    entryCount: rows.length,
    newLanguageCount: mergedLanguages.length - languages.length,
  };
}

export function isLanguageCsvError(error: unknown): error is CsvError {
  return error instanceof CsvError;
}
